import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import { FeedbackType } from '@/lib/definitions/api.types'

export interface FeedbackPrefill {
  type?: FeedbackType
  subject?: string
}

interface FeedbackContextType {
  isFeedbackEnabled: boolean
  isFeedbackOpen: boolean
  feedbackPrefill?: FeedbackPrefill
  openFeedback: (prefill?: FeedbackPrefill) => void
  closeFeedback: () => void
  toggleFeedback: () => void
}

const noop = () => {}

const FeedbackContext = createContext<FeedbackContextType>({
  isFeedbackEnabled: false,
  isFeedbackOpen: false,
  openFeedback: noop,
  closeFeedback: noop,
  toggleFeedback: noop,
})

interface FeedbackProviderProps {
  children: ReactNode
}

export const FeedbackProvider = ({ children }: FeedbackProviderProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [prefill, setPrefill] = useState<FeedbackPrefill | undefined>(undefined)
  const isFeedbackEnabled = useFeatureFlag(FLAGS.FEEDBACK)

  const openFeedback = useCallback((next?: FeedbackPrefill) => {
    setPrefill(next)
    setIsOpen(true)
  }, [])

  const closeFeedback = useCallback(() => {
    setPrefill(undefined)
    setIsOpen(false)
  }, [])

  const toggleFeedback = useCallback(
    () =>
      setIsOpen((current) => {
        if (current) setPrefill(undefined)
        return !current
      }),
    []
  )

  const value = useMemo(
    () => ({
      isFeedbackEnabled,
      isFeedbackOpen: isFeedbackEnabled && isOpen,
      feedbackPrefill: prefill,
      openFeedback,
      closeFeedback,
      toggleFeedback,
    }),
    [
      isFeedbackEnabled,
      isOpen,
      prefill,
      openFeedback,
      closeFeedback,
      toggleFeedback,
    ]
  )

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  )
}

export const useFeedback = (): FeedbackContextType =>
  useContext(FeedbackContext)
